import type { ConversationsResponse } from '@platform/contracts';
import {
  Button,
  Dialog,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SheetContent,
  cn,
} from '@platform/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { ChevronDown, LogOut, Menu, Monitor, Moon, Sun, UserRound } from 'lucide-react';
import { useState } from 'react';

import { api } from '../lib/api';
import { GROUP_LABELS, type ModuleGroup, canAccess, visibleModules } from '../lib/modules';
import { useMe } from '../lib/queries';
import { type ThemePref, useTheme } from '../lib/theme';
import { useScope } from '../lib/use-scope';
import { BrandLogo } from './brand';
import { ImpersonationBanner } from './impersonation';
import { ProfileDialog } from './profile';
import { moduleIcon } from './module-icons';
import { ScopeSelector } from './scope-selector';

/** Conversaciones que esperan a una persona: por responder + derivadas (contador del menú). */
function usePendingCount(): number | null {
  const me = useMe();
  const { scope } = useScope();
  const allowed = canAccess(me, 'inbox:read');
  const q = useQuery({
    queryKey: ['console', scope, 'inbox-count'],
    queryFn: ({ signal }) =>
      api<ConversationsResponse>('/api/inbox/conversations?status=open&pageSize=1', {
        scope,
        signal,
      }),
    enabled: allowed,
    refetchInterval: 60_000,
  });
  return allowed ? (q.data?.counts.open ?? null) : null;
}

function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const me = useMe();
  const { scope } = useScope();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const pending = usePendingCount();
  const modules = visibleModules(me);
  const groups = (['main', 'setup', 'advanced'] as ModuleGroup[]).filter((g) =>
    modules.some((m) => m.group === g),
  );
  return (
    <nav
      aria-label="Módulos"
      className="flex flex-1 flex-col gap-6 overflow-y-auto px-3 pb-6 scrollbar-on-navy"
    >
      {groups.map((g) => (
        <div key={g} className="flex flex-col gap-0.5">
          <p
            className={cn(
              'px-3 pb-1.5 text-xs font-medium text-sidebar-muted',
              g === 'advanced' && 'flex items-center gap-2',
            )}
          >
            {GROUP_LABELS[g]}
            {g === 'advanced' ? (
              <span className="rounded-sm border border-sidebar-border px-1.5 text-[0.65rem] leading-4 font-normal">
                sólo agencia
              </span>
            ) : null}
          </p>
          {modules
            .filter((m) => m.group === g)
            .map((m) => {
              const Icon = moduleIcon(m.id);
              const active = pathname === m.path || pathname.startsWith(`${m.path}/`);
              const count = m.id === 'inbox' ? pending : null;
              return (
                <Link
                  key={m.id}
                  to={m.path}
                  search={{ scope }}
                  onClick={onNavigate}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'group relative flex min-h-10 items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-sidebar-signal',
                    active
                      ? 'bg-linear-to-r from-sidebar-accent to-sidebar-accent/40 font-medium text-sidebar-foreground shadow-[inset_0_1px_0_rgb(255_255_255/0.06)]'
                      : 'text-sidebar-muted hover:bg-sidebar-accent/60 hover:text-sidebar-foreground',
                  )}
                >
                  {/* El "nodo" del logo marca dónde estás. */}
                  <span
                    aria-hidden
                    className={cn(
                      'absolute top-1/2 left-0 h-5 w-1 -translate-y-1/2 rounded-r-full bg-sidebar-signal transition-opacity',
                      active ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <Icon
                    className={cn(
                      'size-[1.05rem] shrink-0 transition-colors',
                      active ? 'text-sidebar-signal' : 'group-hover:text-sidebar-foreground',
                    )}
                    strokeWidth={active ? 2.1 : 1.8}
                    aria-hidden
                  />
                  <span className="flex-1 truncate">{m.label}</span>
                  {count ? (
                    <span
                      className="min-w-5 rounded-full bg-sidebar-signal px-1.5 text-center text-xs leading-5 font-semibold text-brand-navy tabular-nums"
                      aria-label={`${String(count)} por responder`}
                    >
                      {count}
                    </span>
                  ) : null}
                </Link>
              );
            })}
        </div>
      ))}
    </nav>
  );
}

function Brand() {
  return (
    <div className="px-5 pt-6 pb-7">
      <Link
        to="/overview"
        className="inline-flex rounded-md outline-none focus-visible:ring-2 focus-visible:ring-sidebar-signal"
        aria-label="Metamindia · ir al inicio"
      >
        <BrandLogo tone="light" className="h-10" />
      </Link>
    </div>
  );
}

/** Avatar de la persona: su foto o las iniciales sobre azul noche, con el aro cian de la marca. */
function UserAvatar({ name, src }: { name: string; src?: string | null | undefined }) {
  const [failed, setFailed] = useState<string | null>(null);
  const letters = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();
  return (
    <span
      aria-hidden
      className="relative flex size-8 shrink-0 items-center justify-center rounded-full bg-linear-to-br from-brand-navy to-chart-1/70 font-display text-[0.7rem] font-semibold tracking-wide text-white ring-2 ring-sidebar-signal/50 ring-offset-2 ring-offset-background"
    >
      {letters || '·'}
      {src && failed !== src ? (
        <img
          src={src}
          alt=""
          draggable={false}
          onError={() => {
            setFailed(src);
          }}
          className="absolute inset-0 size-full rounded-full object-cover"
        />
      ) : null}
      <span className="absolute -right-0.5 -bottom-0.5 size-2.5 rounded-full bg-brand-green ring-2 ring-background" />
    </span>
  );
}

const THEME_LABEL: Record<ThemePref, string> = {
  light: 'Claro',
  dark: 'Oscuro',
  system: 'Automático',
};

export function AppShell() {
  const me = useMe();
  const { scope, setScope } = useScope();
  const { pref, resolved, setPref } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const logout = async () => {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    qc.clear();
    await navigate({ to: '/login' });
  };

  return (
    <div className="flex min-h-dvh">
      <a
        href="#contenido"
        className="sr-only z-50 rounded-md bg-card px-3 py-2 text-sm focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
      >
        Saltar al contenido
      </a>
      <aside className="hidden w-64 shrink-0 bg-sidebar lg:block">
        <div className="sticky top-0 flex h-dvh flex-col">
          <Brand />
          <SidebarNav />
        </div>
      </aside>

      <Dialog open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side="left"
          className="flex w-72 flex-col bg-sidebar p-0 text-sidebar-foreground"
          closeClassName="text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-foreground"
        >
          <DialogTitle className="sr-only">Menú</DialogTitle>
          <Brand />
          <SidebarNav
            onNavigate={() => {
              setMobileOpen(false);
            }}
          />
        </SheetContent>
      </Dialog>

      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />

      <div className="flex min-w-0 flex-1 flex-col">
        {me.impersonation ? <ImpersonationBanner info={me.impersonation} /> : null}
        <header className="sticky top-0 z-20 flex h-16 items-center gap-2 border-b bg-background/90 px-3 backdrop-blur sm:gap-3 sm:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 lg:hidden"
            aria-label="Abrir menú"
            onClick={() => {
              setMobileOpen(true);
            }}
          >
            <Menu />
          </Button>
          <ScopeSelector me={me} value={scope} onChange={setScope} />
          <div className="ml-auto flex shrink-0 items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label={`Tema: ${THEME_LABEL[pref]}`}>
                  {resolved === 'dark' ? <Moon /> : <Sun />}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Apariencia</DropdownMenuLabel>
                <DropdownMenuRadioGroup
                  value={pref}
                  onValueChange={(v) => {
                    setPref(v as ThemePref);
                  }}
                >
                  <DropdownMenuRadioItem value="light">
                    <Sun /> Claro
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="dark">
                    <Moon /> Oscuro
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="system">
                    <Monitor /> Automático
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-11 gap-2.5 rounded-full py-1 pr-2 pl-1 sm:pr-3"
                  aria-label="Mi cuenta"
                >
                  <UserAvatar name={me.user.name} src={me.user.avatarUrl} />
                  <span className="hidden min-w-0 flex-col items-start leading-tight md:flex">
                    <span className="max-w-40 truncate text-sm font-medium">{me.user.name}</span>
                    <span className="max-w-40 truncate text-xs font-normal text-muted-foreground">
                      {me.user.email}
                    </span>
                  </span>
                  <ChevronDown className="hidden text-muted-foreground md:block" aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel className="flex items-center gap-3 font-normal">
                  <UserAvatar name={me.user.name} src={me.user.avatarUrl} />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-foreground">
                      {me.user.name}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {me.user.email}
                    </span>
                  </span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => {
                    setProfileOpen(true);
                  }}
                >
                  <UserRound /> Mi perfil
                </DropdownMenuItem>
                <DropdownMenuItem
                  variant="destructive"
                  onSelect={() => {
                    void logout();
                  }}
                >
                  <LogOut /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main
          id="contenido"
          tabIndex={-1}
          className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 outline-none sm:px-6 lg:px-8 lg:py-8"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
